import {
  Button,
  Fieldset,
  FileButton,
  Group,
  Image,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { TransformedValues, useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BatchesImportFileParams } from '../../lib/apis/contracts';

import { useCampaigns, useSharedQueries } from '@data-access';

const root = import.meta.env.VITE_API_ROOT;

interface ImportFieldsFormProps {
  onSubmit: (values: Omit<BatchesImportFileParams, 'contractId'>) => void;
  onExport?: (values: Omit<BatchesImportFileParams, 'contractId' | 'file'>) => void;
  onCancel?: () => void;
  pending?: boolean;
}

export const ImportFieldsForm: React.FC<ImportFieldsFormProps> = ({
  onSubmit,
  onExport,
  onCancel,
  pending = false,
}) => {
  const { t } = useTranslation();
  const form = useForm({
    initialValues: {
      connectivityId: null as number | null,
      campaign: '',
      file: null as File | null,
    },
    validate: {
      connectivityId: (value) =>
        value ? null : 'Please select a connectivity',
      campaign: (value) => (value ? null : t('form.campaign-field-required')),
      file: (value) => (value ? null : t('form.file-field-required')),
    },
    transformValues: (values) => ({
      campaign: Number(values.campaign),
      file: values.file as File,
      connectivityId: values.connectivityId as number,
    }),
  });
  const campaigns = useCampaigns();

  const sharedQueries = useSharedQueries();

  const connectivity = useQuery(sharedQueries.connectivity());

  type Transformed = TransformedValues<typeof form>;

  const handleSubmit = (values: Transformed) => {
    onSubmit(values);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Select
        data={campaigns.data?.map((c) => ({
          value: c.id.toString(),
          label: c.name,
        }))}
        label={t('common.campaign')}
        required
        {...form.getInputProps('campaign')}
      />

      {connectivity.data?.connectivity.map((c) => (
        <Fieldset key={c.id} mt="md">
          <Image alt={c.name} fit="contain" h={50} src={`${root}${c.logo}`} />
          <Text flex="1 1 auto" mt="sm" ta="justify">
            {c.description}
          </Text>

          <Stack>
            <FileButton
              accept=".zip,.rar,.7z,.gz,.xml"
              onChange={(file) => {
                form.setValues({
                  connectivityId: c.id,
                  file: file,
                });
              }}
            >
              {(p) => (
                <Button {...p} mt="xs">
                  {t('uploadYieldMapForm.selectFile')}
                </Button>
              )}
            </FileButton>
            {onExport && form.values.campaign && form.values.connectivityId === c.id ? (
              <Button
                onClick={() => {
                  onExport({
                    connectivityId: c.id,
                    campaign: Number(form.values.campaign),
                  });
                }}
                size="compact-sm"
                variant="light"
              >
                {t('common.export')}
              </Button>
            ) : null}
            {c.tutorial ? (
              <Button
                component="a"
                href={root + c.tutorial}
                rel="noreferrer"
                size="compact-sm"
                target="_blank"
                variant="light"
              >
                {t('common.notice-dutilisation')}
              </Button>
            ) : null}
          </Stack>

          {form.values.connectivityId === c.id && form.values.file ? (
            <span>{form.values.file.name}</span>
          ) : null}
        </Fieldset>
      ))}

      <Group mt="md">
        <Button loading={pending} type="submit">
          {t('common.importer')}
        </Button>
        <Button
          color="red"
          onClick={() => {
            form.reset();
            onCancel?.();
          }}
          type="button"
        >
          {t('common.cancel')}
        </Button>
      </Group>
    </form>
  );
};
